// Shared by the application and the standalone maintenance page; no app state.
export const UPDATE_STEPS = ['Vorbereitung des Updates', 'Aktuellen Stand sichern', 'Daten aktualisieren', 'Neue Version starten', 'Aktualisierung prüfen', 'KS Studio öffnen'];
export function remainingText(progress, now = Date.now()) {
  const end = Number(progress?.estimatedFinishAt);
  if (!end || !Number.isFinite(end)) return 'Voraussichtliche Updatedauer wird ermittelt …';
  if (now >= end) return 'Das Update benötigt etwas mehr Zeit.';
  const seconds = Math.ceil((end - now) / 1000);
  return `Voraussichtliche Updatedauer: noch etwa ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')} Minuten`;
}

export async function readReleaseStatus(fetcher, now = Date.now(), { active = true } = {}) {
  const readApi = async () => {
    const response = await fetcher('/api/release/status', { cache: 'no-store', signal: AbortSignal.timeout(4000) });
    if (!response.ok) throw new Error('Status temporarily unavailable');
    const state = await response.json();
    if (state.protocol !== 1 || !['disabled', 'idle', 'announce', 'drain', 'maintenance', 'cancelled'].includes(state.phase)) throw new Error('Unknown status protocol');
    return state;
  };
  // Establish whether updates are enabled before asking for an optional file.
  // Dev stops here; ordinary Stable operation only polls the authoritative API.
  const initial = active ? null : await readApi();
  if (initial && !['announce', 'drain', 'maintenance'].includes(initial.phase)) return initial;
  let publicState;
  try {
    const response = await fetcher('/release-progress.json', { cache: 'no-store', signal: AbortSignal.timeout(4000) });
    if (response.ok && response.status !== 204) {
      const value = await response.json();
      if (value.protocol === 1 && value.progress && value.updatedAt <= now + 5000) publicState = value;
    }
  } catch { /* Old frontend or brief frontend restart: try the existing API. */ }
  // Active, recently published status remains available with the backend stopped.
  if (publicState && now - publicState.updatedAt < 15000 && ['announce', 'drain', 'maintenance'].includes(publicState.phase)) return publicState;
  let state;
  try { state = initial || await readApi(); }
  catch (error) {
    if (publicState?.progress.failed && ['drain', 'maintenance'].includes(publicState.phase)) return publicState;
    throw error;
  }
  return publicState && publicState.run === state.run && publicState.phase === state.phase
    ? { ...state, progress: publicState.progress } : state;
}

export function createReleaseMaintenanceView({ logo, onDeadline = () => { /* Optional in the standalone page. */ } } = {}) {
  const overlay = document.createElement('div');
  overlay.className = 'release-update-overlay';
  overlay.hidden = true;
  overlay.innerHTML = `<section class="release-update-card" role="dialog" aria-modal="true" aria-labelledby="releaseUpdateTitle">
    <img class="release-update-logo" alt="KS Studio">
    <h1 id="releaseUpdateTitle"></h1><p class="release-update-message"></p>
    <p class="release-update-time" role="timer" aria-live="off"></p>
    <div class="release-update-progress" role="progressbar" aria-label="Fortschritt der Aktualisierung" aria-valuemin="0" aria-valuemax="100"></div>
    <ol class="release-update-steps"></ol><p class="release-update-note" role="status" aria-live="polite"></p>
    <button type="button" class="release-update-continue">Weiterarbeiten</button></section>`;
  const banner = document.createElement('button');
  banner.type = 'button'; banner.className = 'release-update-banner'; banner.hidden = true;
  const find = selector => overlay.querySelector(selector);
  find('img').src = logo || document.querySelector('img.ks-header-logo, img.auth-gate-logo')?.src || '/release-logo.svg';
  const title = find('h1'), message = find('.release-update-message'), timer = find('.release-update-time');
  const bar = find('.release-update-progress'), list = find('ol'), note = find('.release-update-note'), button = find('button');
  for (const text of UPDATE_STEPS) {
    const segment = document.createElement('span'); bar.append(segment);
    const item = document.createElement('li'); item.textContent = text; list.append(item);
  }
  document.body.append(overlay, banner);
  let state = null, dismissed = false, run = null, deadlineHandled = false, previousFocus;
  button.onclick = () => { dismissed = true; overlay.hidden = true; banner.hidden = state?.phase !== 'announce'; previousFocus?.focus?.(); };
  banner.onclick = () => { dismissed = false; overlay.hidden = false; banner.hidden = true; button.focus(); };
  const refresh = () => {
    if (!state) return;
    if (state.phase === 'announce') {
      const seconds = Math.max(0, Math.ceil((state.deadline - Date.now()) / 1000));
      title.textContent = `KS Studio wird in ${seconds} Sekunden aktualisiert`;
      timer.textContent = `Update beginnt in ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
      banner.textContent = timer.textContent;
      if (seconds === 0 && !deadlineHandled) { deadlineHandled = true; onDeadline(); }
    } else if (['drain', 'maintenance'].includes(state.phase)) {
      timer.textContent = remainingText(state.progress);
    }
  };
  const onKey = event => {
    if (overlay.hidden || button.hidden) return;
    if (event.key === 'Tab') { event.preventDefault(); button.focus(); }
    if (event.key === 'Escape') { event.preventDefault(); button.click(); }
  };
  document.addEventListener('keydown', onKey);
  const interval = setInterval(refresh, 250);
  return {
    update(next, { message: customMessage = '', error = false } = {}) {
      if (next.run !== run || (next.phase === 'announce' && state?.phase !== 'announce')) {
        dismissed = false; deadlineHandled = false; run = next.run;
      }
      const first = overlay.hidden && banner.hidden;
      state = next;
      if (next.phase === 'idle' || next.phase === 'disabled') { overlay.hidden = true; banner.hidden = true; return; }
      const announcement = next.phase === 'announce', cancelled = next.phase === 'cancelled';
      overlay.hidden = (announcement || cancelled) && dismissed;
      banner.hidden = !announcement || !overlay.hidden;
      button.hidden = !announcement && !cancelled;
      button.textContent = 'Weiterarbeiten';
      title.textContent = cancelled ? 'Die Aktualisierung wurde verschoben' : 'KS Studio wird aktualisiert';
      message.textContent = (!error && customMessage) || (announcement
        ? 'Ihre Arbeit wird automatisch gespeichert. Nach Ablauf der Zeit wird KS Studio vorübergehend gesperrt.'
        : cancelled ? 'Sie können weiterarbeiten.'
          : 'KS Studio ist während des Updates nicht verfügbar. Sobald die Aktualisierung abgeschlossen ist, öffnet sich KS Studio automatisch.');
      bar.hidden = list.hidden = announcement || cancelled;
      timer.hidden = cancelled || error || Boolean(next.progress?.failed);
      const step = Math.max(0, Math.min(5, Number(next.progress?.step) || 0));
      const percent = Math.max(0, Math.min(100, Number(next.progress?.percent) || 0));
      bar.setAttribute('aria-valuenow', String(percent));
      bar.setAttribute('aria-valuetext', `${UPDATE_STEPS[step]}, ${percent} Prozent`);
      [...bar.children].forEach((segment, i) => {
        segment.className = i < step ? 'is-done' : i === step ? 'is-current' : '';
        segment.style.flexGrow = String(next.progress?.weights?.[i] || 1);
      });
      [...list.children].forEach((item, i) => { item.className = i < step ? 'is-done' : i === step ? 'is-current' : ''; if (i === step) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current'); });
      const text = error ? customMessage : next.progress?.failed ? 'Das Update wurde unterbrochen. KS Studio bleibt vorübergehend gesperrt.' : '';
      if (note.textContent !== text) note.textContent = text;
      note.hidden = !text;
      if (first) { previousFocus = document.activeElement; if (announcement || cancelled) button.focus(); }
      refresh();
    },
    destroy() { clearInterval(interval); document.removeEventListener('keydown', onKey); overlay.remove(); banner.remove(); }
  };
}
